/**
 * Fechar ticket com motivo (spec 054). A mesa com motivo ativo exige um; a sem
 * motivo fecha como sempre, com a observação opcional. Quem barra é a API
 * (close_reason_required): a tela só não deixa enviar sem escolher.
 *
 * `CloseReasonFields` é o pedaço que se repete: a tela do ticket e a de
 * atendimento usam o `CloseTicketDialog`, e o "Concluir e encerrar" da agenda
 * põe os mesmos campos no diálogo dele.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TICKET_CLOSE_NOTE_MAX, type TicketCloseBody, type TicketCloseOptions } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Label, Select, Textarea } from '@/components/ui/input';
import { Dialog, Spinner } from '@/components/ui/misc';
import { ticketCloseOptionsQuery } from './tickets-queries';
import { useCloseTicket } from './tickets-mutations';
import { errorMessage } from './ticket-format';

export interface CloseReasonValue {
  /** o id do motivo como texto do select; '' = nenhum escolhido */
  reasonId: string;
  note: string;
}

export const EMPTY_CLOSE_REASON: CloseReasonValue = { reasonId: '', note: '' };

/** O corpo do POST: só o que foi escolhido ou escrito. */
export function closeBodyOf(value: CloseReasonValue): TicketCloseBody {
  const body: TicketCloseBody = {};
  if (value.reasonId !== '') body.reasonId = Number(value.reasonId);
  if (value.note.trim() !== '') body.note = value.note.trim();
  return body;
}

/**
 * Falta escolher o motivo que a mesa exige? Enquanto carrega, também falta.
 * Se a lista não carregou (API anterior à spec 054, falha passageira), não
 * falta: a tela não trava o fechamento, e quem decide é a API.
 */
export function closeReasonMissing(
  options: TicketCloseOptions | undefined,
  value: CloseReasonValue,
  failed = false
): boolean {
  if (failed) return false;
  if (!options) return true;
  return options.required && value.reasonId === '';
}

export function CloseReasonFields({
  idPrefix,
  options,
  loading,
  value,
  onChange,
  disabled,
}: {
  idPrefix: string;
  options: TicketCloseOptions | undefined;
  loading: boolean;
  value: CloseReasonValue;
  onChange: (value: CloseReasonValue) => void;
  disabled?: boolean;
}) {
  const reasons = options?.reasons ?? [];
  return (
    <>
      {loading || reasons.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-reason`} className="text-[13px] font-semibold">
            Motivo do fechamento<span className="-ml-1 text-destructive">*</span>
          </Label>
          <Select
            id={`${idPrefix}-reason`}
            className="h-8"
            value={value.reasonId}
            onChange={(e) => onChange({ ...value, reasonId: e.target.value })}
            disabled={loading || disabled}
          >
            <option value="">{loading ? 'Carregando…' : 'Selecione…'}</option>
            {reasons.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
        </div>
      ) : null}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-note`} className="text-[13px] font-semibold">
          Observação
        </Label>
        <Textarea
          id={`${idPrefix}-note`}
          rows={3}
          maxLength={TICKET_CLOSE_NOTE_MAX}
          value={value.note}
          onChange={(e) => onChange({ ...value, note: e.target.value })}
          disabled={disabled}
          placeholder="Opcional. Fica no histórico do ticket."
        />
      </div>
    </>
  );
}

export function CloseTicketDialog({
  ticketNumber,
  onClose,
  onClosed,
}: {
  ticketNumber: number;
  onClose: () => void;
  /** depois de fechado (a tela de atendimento segue para o próximo) */
  onClosed?: () => void;
}) {
  const optionsQ = useQuery(ticketCloseOptionsQuery(ticketNumber));
  const close = useCloseTicket(ticketNumber);
  const [value, setValue] = useState<CloseReasonValue>(EMPTY_CLOSE_REASON);
  const missing = closeReasonMissing(optionsQ.data, value, optionsQ.isError);

  return (
    <Dialog open onClose={onClose} title="Fechar ticket">
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (missing) return;
          close.mutate(closeBodyOf(value), {
            onSuccess: () => {
              onClose();
              onClosed?.();
            },
          });
        }}
      >
        <p className="text-sm text-muted-foreground">
          O ticket #{ticketNumber} será marcado como fechado. Você poderá reabri-lo depois, se necessário.
        </p>
        {optionsQ.isError ? (
          <p className="text-sm text-muted-foreground">
            Não deu para carregar os motivos desta mesa. Se ela exigir um, o fechamento vai avisar.
          </p>
        ) : (
          <CloseReasonFields
            idPrefix="close-ticket"
            options={optionsQ.data}
            loading={optionsQ.isPending}
            value={value}
            onChange={setValue}
            disabled={close.isPending}
          />
        )}
        {close.isError ? (
          <p className="text-sm text-destructive" role="alert">
            {errorMessage(close.error)}
          </p>
        ) : null}
        <div className="mt-1 flex justify-end gap-2 border-t pt-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Voltar
          </Button>
          <Button type="submit" size="sm" disabled={close.isPending || missing}>
            {close.isPending ? <Spinner className="size-4 border-primary-foreground" /> : null}
            Fechar ticket
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
